import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { showNotification, useQueryFetch } from 'sonata-react-components';

import { AdvertiserTab } from '@/components/campaigns/AdvertiserTab';
import { CampaignPlanTab } from '@/components/campaigns/CampaignPlanTab';
import { EntityListAssistantChat } from '@/components/campaigns/EntityListAssistantChat';
import { InsertionOrderTab } from '@/components/campaigns/InsertionOrderTab';
import { LineItemsTab } from '@/components/campaigns/LineItemsTab';
import { GlobalCatch } from '@/components/common/GlobalCatch';
import { useEntityCreatedSocket } from '@/hooks/useEntityCreatedSocket';
import { CampaignsStore, useStore as useCampaignStore } from '@/store/local/campaigns';

import './index.scss';

export const Catch = () => {
  return <GlobalCatch />;
};

export default function Campaigns() {
  const { formatMessage } = useIntl();
  const [searchParams] = useSearchParams();
  const {
    countLineItems,
    getAdvertisers,
    getCampaigns,
    getInsertionOrders,
    setCacheSeed,
    setHasAdvertisers,
    setHasCampaigns,
    setHasInsertionOrders,
    setHasLineItems
  } = useCampaignStore() as CampaignsStore;

  useEntityCreatedSocket({
    onEntityCreated: () => {
      setHasAdvertisers(true);
      setCacheSeed();
    },
    socketEventName: 'advertiser-ai-creation'
  });
  useEntityCreatedSocket({
    onEntityCreated: () => {
      setHasCampaigns(true);
      setCacheSeed();
    },
    socketEventName: 'campaign-plan-ai-creation'
  });
  useEntityCreatedSocket({
    onEntityCreated: () => {
      setHasInsertionOrders(true);
      setCacheSeed();
      showNotification(
        'notify',
        formatMessage({
          defaultMessage: 'Insertion Order created successfully',
          id: 'insertionOrders.notifications.created'
        })
      );
    },
    socketEventName: 'insertion-order-ai-creation'
  });
  useEntityCreatedSocket({
    onEntityCreated: () => {
      setHasLineItems(true);
      setCacheSeed();
      showNotification(
        'notify',
        formatMessage({ defaultMessage: 'Line Item created successfully', id: 'lineItems.notifications.created' })
      );
    },
    socketEventName: 'line-item-ai-creation'
  });

  const getAdvertisersToken: Record<string, any> = {};
  const getCampaignsToken: Record<string, any> = {};
  const getInsertionOrdersToken: Record<string, any> = {};
  const countLineItemsToken: Record<string, any> = {};

  useQueryFetch(['getAdvertisers'], async () => getAdvertisers(getAdvertisersToken), {
    enabled: true,
    staleTime: 0
  });

  useQueryFetch(['getCampaigns'], async () => getCampaigns(getCampaignsToken), {
    enabled: true,
    staleTime: 0
  });

  useQueryFetch(['getInsertionOrders'], async () => getInsertionOrders(getInsertionOrdersToken), {
    enabled: true,
    staleTime: 0
  });

  useQueryFetch(['countLineItems'], async () => countLineItems(countLineItemsToken), {
    enabled: true,
    staleTime: 0
  });

  const defaultTab = 'lineItem';
  const currentTab = searchParams.get('tab') || defaultTab;

  return (
    <>
      <div className="campaignsContent">
        {currentTab === 'advertiser' && <AdvertiserTab />}
        {currentTab === 'campaignPlan' && <CampaignPlanTab />}
        {currentTab === 'insertionOrder' && <InsertionOrderTab />}
        {currentTab === 'lineItem' && <LineItemsTab />}
      </div>
      <EntityListAssistantChat
        agentId="campaign_list_mtf"
        buttonText={formatMessage({ defaultMessage: 'Smart Assistant', id: 'main.aiChat.buttonText' })}
        id="campaignsListAssistant"
        startUpMessageText={formatMessage({
          defaultMessage: "Looking to create a new entity? I'm here to assist.",
          id: 'main.aiChat.startUpMessage'
        })}
      />
    </>
  );
}
